import { useEffect, useMemo, useRef } from "react";
import { gsap } from "@/lib/gsap";
import { COUPLE, WEDDING, EVENTS, HIGHLIGHT_EVENTS } from "@/lib/wedding";
import { IS_GROOM } from "@/lib/side";

/**
 * VarmalaScroll
 * -----------------------------------------------------------
 * Pinned, scroll-scrubbed canvas frame sequence (varmala exchange)
 * with four text layers that blur-swap over it.
 */
// Phones get a lighter set (every 2nd frame, 1280px, look baked in by
// scripts/bake-varmala-frames.py) and skip the live filter + edge blur:
// 161 decoded 1080p frames (~1 GB) plus per-frame filters made them stutter.
const IS_PHONE =
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px), (pointer: coarse)").matches;
// Default: the full varmala exchange (our Gemini clips edited together:
// bride garlands groom, groom garlands bride, they hold hands), 96 frames
// at 8 fps in /varmala/exchange. Backups, still playable:
//   ?varmala=clip     first Gemini clip (/varmala/clip, 90 frames)
//   ?varmala=classic  old reference frames (frames + frames-m)
const VARMALA =
  typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("varmala");
const CLASSIC = VARMALA === "classic";
const SOURCES = { exchange: ["exchange", 96], clip: ["clip", 90] };
const [CLIP_DIR, CLIP_COUNT] = SOURCES[VARMALA] || SOURCES.exchange;
const FRAME_COUNT = !CLASSIC ? CLIP_COUNT : IS_PHONE ? 81 : 161;
const FRAME_DIR = !CLASSIC ? CLIP_DIR : IS_PHONE ? "frames-m" : "frames";
// the live tone filter + edge blur only belong to the classic laptop frames
const LIVE_FX = CLASSIC && !IS_PHONE;
const frameUrl = (i) => `/varmala/${FRAME_DIR}/frame-${String(i).padStart(3, "0")}.jpg`;

// Load a coarse pass first (every 8th, 4th, 2nd frame) so scrubbing works early
const LOAD_ORDER = (() => {
  const seen = new Set();
  const order = [];
  for (const step of [8, 4, 2, 1]) {
    for (let i = 0; i < FRAME_COUNT; i += step) if (!seen.has(i)) { seen.add(i); order.push(i); }
  }
  return order;
})();

// no backdrop blur on phones (it re-blurs the moving canvas every frame)
export const GLASS =
  "w-full max-w-3xl mx-auto text-center bg-[rgba(74,14,34,0.55)] md:bg-[rgba(74,14,34,0.35)] md:backdrop-blur-sm border border-gold/30 rounded-2xl px-6 py-8 sm:px-12 sm:py-10";
// Gold-foil gradient lettering for the couple's names
export const GOLD_TEXT = {
  backgroundImage: "linear-gradient(180deg, #F6E7B8 0%, #E8CC7E 45%, #C9A961 75%, #E8CC7E 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

// Tone applied to every varmala frame (CSS filter on the canvas)
const FRAME_FILTER = "brightness(0.85) contrast(1.12) saturate(1.1)";

export const TEXT_SHADOW = { filter: "drop-shadow(0 4px 10px rgba(58,10,24,0.7))" };


export default function VarmalaScroll() {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const indicatorRef = useRef(null);
  const l1 = useRef(null);
  const l2 = useRef(null);
  const l3 = useRef(null);
  const l4 = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section) return;
    const ctx2d = canvas.getContext("2d");

    const images = new Array(FRAME_COUNT).fill(null);
    let current = 0;
    let drawn = null; // last image painted, so repeated scroll ticks cost nothing
    let disposed = false;

    const draw = (n, force = false) => {
      current = n;
      // nearest loaded lower frame
      let img = null;
      for (let i = n; i >= 0; i--) {
        const c = images[i];
        if (c && c.complete && c.naturalWidth) { img = c; break; }
      }
      if (!img || (img === drawn && !force)) return;
      drawn = img;
      const cw = canvas.width;
      const ch = canvas.height;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const cover = Math.max(cw / iw, ch / ih);
      const fit = Math.min(cw / iw, ch / ih);
      // An upright clip on a wide screen: a dim cover copy fills the sides,
      // the full frame sits sharp in the middle. Otherwise just cover.
      if (ih > iw && cw > ch) {
        ctx2d.globalAlpha = 0.35;
        ctx2d.drawImage(img, (cw - iw * cover) / 2, (ch - ih * cover) / 2, iw * cover, ih * cover);
        ctx2d.globalAlpha = 1;
        ctx2d.drawImage(img, (cw - iw * fit) / 2, (ch - ih * fit) / 2, iw * fit, ih * fit);
      } else {
        ctx2d.drawImage(img, (cw - iw * cover) / 2, (ch - ih * cover) / 2, iw * cover, ih * cover);
      }
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, IS_PHONE ? 1.5 : 2);
      canvas.width = Math.round(section.clientWidth * dpr);
      canvas.height = Math.round(section.clientHeight * dpr);
      draw(current, true);
    };

    const load = (i) => {
      const img = new Image();
      img.onload = () => {
        if (disposed) return;
        // decode off the scroll path so drawImage doesn't stall mid-scrub
        img.decode?.().catch(() => {});
        if (i === 0) draw(0, true);
      };
      img.src = frameUrl(i + 1);
      images[i] = img;
    };

    resize();
    LOAD_ORDER.forEach(load);
    window.addEventListener("resize", resize);

    const ctx = gsap.context(() => {
      const state = { frame: 0 };
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: IS_PHONE ? "+=380%" : "+=500%",
          scrub: 0.5,
          pin: true,
          anticipatePin: 1,
        },
      });

      tl.to(state, {
        frame: FRAME_COUNT - 1,
        snap: "frame",
        ease: "none",
        duration: 4,
        onUpdate: () => draw(Math.round(state.frame)),
      }, 0);
      tl.to(indicatorRef.current, { opacity: 0, duration: 0.2 }, 0.1);

      // animated blur is too heavy on phones; they just fade and slide
      const blur = (px) => (IS_PHONE ? {} : { filter: `blur(${px}px)` });
      const out = { opacity: 0, ...blur(20), y: -80, duration: 0.6 };
      const from = { opacity: 0, ...blur(20), y: 80 };
      const to = { opacity: 1, ...blur(0), y: 0, duration: 0.6, ease: "power2.out" };

      tl.to(l1.current, { ...out, ease: "power2.inOut" }, 0.35);
      tl.fromTo(l2.current, from, to, 0.85);
      tl.to(l2.current, { ...out, ease: "power2.in" }, 1.7);
      tl.fromTo(l3.current, from, to, 2.2);
      tl.to(l3.current, { ...out, ease: "power2.in" }, 3.1);
      tl.fromTo(l4.current, from, to, 3.4);

      // Intro stagger for L1 children, once
      gsap.from(l1.current.children, {
        opacity: 0, y: 20, filter: "blur(10px)",
        stagger: 0.15, duration: 1, ease: "power3.out", delay: 0.2,
        scrollTrigger: { trigger: section, start: "top 75%", once: true },
      });
    }, section);

    return () => {
      disposed = true;
      window.removeEventListener("resize", resize);
      ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} id="varmala" className="relative w-full h-screen overflow-hidden bg-ink">
      {/* FRAME_FILTER tones every frame at once: slightly darker, richer, more contrast */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={LIVE_FX ? { filter: FRAME_FILTER } : undefined} />

      {/* Soft blur toward the edges, sharp in the centre (baked into the phone frames) */}
      {LIVE_FX && (
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          maskImage: "radial-gradient(ellipse at center, transparent 50%, #000 85%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, transparent 50%, #000 85%)",
        }}
      />
      )}

      {/* Overlays: vignette + bottom wash (wine, never black) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(42,8,20,0) 35%, rgba(42,8,20,0.55) 70%, rgba(30,6,16,0.9) 100%)" }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(to top, rgba(74,14,34,0.7), transparent 40%)" }}
      />

      <FallingPetals count={IS_PHONE ? 12 : 22} />
      <SparkleDust count={IS_PHONE ? 16 : 40} />

      <div className="absolute inset-0 pointer-events-none">
        {/* L1 - Intro shloka */}
        <div ref={l1} className="absolute inset-0 flex items-center justify-center px-4">
          <VarmalaL1 />
        </div>

        {/* L2 - Sacred vows */}
        <div ref={l2} className="absolute inset-0 flex items-center justify-center px-4" style={{ opacity: 0 }}>
          <VarmalaL2 />
        </div>

        {/* L3 - Shubh Muhurat */}
        <div ref={l3} className="absolute inset-0 flex items-center justify-center px-4" style={{ opacity: 0 }}>
          <VarmalaL3 />
        </div>

        {/* L4 - Invitation CTA */}
        <div ref={l4} className="absolute inset-0 flex items-center justify-center px-4 pointer-events-auto" style={{ opacity: 0 }}>
          <VarmalaL4 />
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        ref={indicatorRef}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-ivory/70 pointer-events-none"
      >
        <span className="font-body text-[10px] uppercase tracking-chip">Scroll</span>
        <svg className="animate-bounce" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 6 L 8 11 L 13 6" />
        </svg>
      </div>
    </section>
  );
}

/* ----------- Text layers (shared with VarmalaVideo) ----------- */
export function VarmalaL1() {
  const { groom, bride } = COUPLE;
  const [first, second] = IS_GROOM ? [groom, bride] : [bride, groom];
  return (
    <div className={`${GLASS} flex flex-col items-center gap-3 sm:gap-4`} style={TEXT_SHADOW}>
      <img
        src="/ornaments/ganesh.png"
        alt=""
        className="w-16 sm:w-24"
        style={{ filter: "drop-shadow(0 0 14px rgba(232,204,126,0.7))" }}
      />
      <div className="font-hindi text-gold-light text-base sm:text-xl">॥ श्री गणेशाय नमः ॥</div>
      <div className="font-hindi text-gold-light text-base sm:text-xl leading-relaxed">
        वक्रतुण्ड महाकाय सूर्यकोटि समप्रभ।<br />
        निर्विघ्नं कुरु मे देव सर्वकार्येषु सर्वदा॥
      </div>
      <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.3em] text-ivory/80">
        The honour of your presence is requested at the Shubh Vivah of
      </p>
      <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] flex flex-col sm:flex-row sm:gap-4 items-center">
        <span className="text-gold-light" style={GOLD_TEXT}>{first.name}</span>
        <img src="/brand/logo.png" alt="&" className="h-[0.55em] w-auto self-center my-1 sm:my-0 sm:mx-1 drop-shadow-[0_2px_8px_rgba(58,10,24,0.6)]" />
        <span className="text-gold-light" style={GOLD_TEXT}>{second.name}</span>
      </h1>
      <p className="font-display italic text-gold-light text-lg sm:text-2xl">
        {WEDDING.dateLabel} · {WEDDING.venue.name}, Jaipur
      </p>
    </div>
  );
}

export function VarmalaL2() {
  return (
    <div className={`${GLASS} flex flex-col items-center gap-4`} style={TEXT_SHADOW}>
      <h2 className="font-display text-gold-light text-4xl sm:text-6xl leading-tight" style={GOLD_TEXT}>Bound by the Sacred Fire</h2>
      <p className="font-body text-ivory/90 leading-loose text-sm sm:text-base">
        Blessed by family. Seven promises, spoken before Agni and the people we love most.
      </p>
      <p className="font-display italic text-gold-light uppercase tracking-widest text-sm sm:text-lg">
        Agni as Witness • Love as Guide
      </p>
    </div>
  );
}

export function VarmalaL3() {
  const highlights = HIGHLIGHT_EVENTS.map((k) => EVENTS.find((e) => e.key === k)).filter(Boolean);
  return (
    <div className={`${GLASS} flex flex-col items-center gap-4`} style={TEXT_SHADOW}>
      <h2 className="font-display text-gold-light text-4xl sm:text-6xl leading-tight" style={GOLD_TEXT}>Shubh Muhurat</h2>
      <p className="font-display italic text-gold-light text-lg sm:text-xl">{WEDDING.muhuratLabel}</p>
      {/* one row per day (20 Nov, then 21 Nov), every card the same width */}
      <div className="flex flex-col gap-2 sm:gap-3 w-full">
        {[...new Set(highlights.map((e) => e.date))].map((date) => (
          <div key={date} className="flex justify-center gap-2 sm:gap-3">
            {highlights.filter((e) => e.date === date).map((e) => (
              <div key={e.key} className="basis-[calc(33.333%-0.5rem)] sm:basis-[calc(33.333%-0.75rem)] grow-0 shrink-0 flex flex-col items-center border border-gold/30 rounded-xl bg-[rgba(74,14,34,0.35)] px-2 py-3 sm:p-4">
                {/* fixed two-line label area so times and dates line up across cards */}
                <div className="min-h-[2.6em] flex items-center justify-center text-center font-body text-[10px] uppercase tracking-[0.2em] leading-tight text-ivory/85">{e.short || e.name}</div>
                <div className="font-display text-gold-light text-lg sm:text-2xl mt-1">{e.shortTime || e.time}</div>
                <div className="font-body text-[10px] sm:text-xs text-ivory/75 mt-0.5">{e.date}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function VarmalaL4() {
  return (
    <div className={`${GLASS} flex flex-col items-center gap-4`} style={TEXT_SHADOW}>
      <h3 className="font-display text-gold-light text-4xl sm:text-6xl leading-tight" style={GOLD_TEXT}>Join Our Celebration</h3>
      <p className="font-body text-ivory/90 text-sm sm:text-base">
        Your blessings and presence will make our day complete.
      </p>
      <p className="font-display italic text-gold-light text-xl sm:text-2xl" style={GOLD_TEXT}>#SonaKaRaj</p>
    </div>
  );
}


/* ----------- Rose & marigold petals falling softly ----------- */
const PETAL_COLORS = ["#C2185B", "#E91E63", "#F48FB1", "#E8752A", "#F6A623"];
export function FallingPetals({ count = 22 }) {
  const petals = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: Math.random() * 100,
        size: 8 + Math.random() * 8,
        duration: 9 + Math.random() * 8,
        delay: -Math.random() * 17,
        drift: (Math.random() < 0.5 ? -1 : 1) * (30 + Math.random() * 60),
        spin: 180 + Math.random() * 360,
        color: PETAL_COLORS[i % PETAL_COLORS.length],
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden motion-reduce:hidden" aria-hidden="true">
      {petals.map((p, i) => (
        <svg
          key={i}
          viewBox="0 0 20 28"
          className="absolute top-0"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 1.4,
            opacity: 0.8,
            "--drift": `${p.drift}px`,
            "--spin": `${p.spin}deg`,
            animation: `petal-drop ${p.duration}s linear ${p.delay}s infinite`,
          }}
        >
          <path d="M10 0 C18 8 18 20 10 28 C2 20 2 8 10 0Z" fill={p.color} />
        </svg>
      ))}
      <style>{`
        @keyframes petal-drop {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); }
          50%  { transform: translate3d(var(--drift), 50vh, 0) rotate(calc(var(--spin) / 2)); }
          100% { transform: translate3d(calc(var(--drift) * -0.4), 110vh, 0) rotate(var(--spin)); }
        }
      `}</style>
    </div>
  );
}

/* ----------- Gold sparkle dust twinkling and rising ----------- */
export function SparkleDust({ count = 40 }) {
  const specks = useMemo(
    () =>
      Array.from({ length: count }, () => ({
        left: Math.random() * 100,
        top: 20 + Math.random() * 80,
        size: 1.5 + Math.random() * 2.5,
        duration: 5 + Math.random() * 6,
        delay: -Math.random() * 11,
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden motion-reduce:hidden" aria-hidden="true">
      {specks.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: d.size,
            height: d.size,
            background: "#F6E7B8",
            boxShadow: `0 0 ${d.size * 3}px rgba(232,204,126,0.9)`,
            animation: `sparkle-rise ${d.duration}s ease-in-out ${d.delay}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes sparkle-rise {
          0%   { opacity: 0;   transform: translate3d(0, 0, 0) scale(0.6); }
          30%  { opacity: 1; }
          70%  { opacity: 0.8; }
          100% { opacity: 0;   transform: translate3d(0, -14vh, 0) scale(1.1); }
        }
      `}</style>
    </div>
  );
}
